import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { trackSignupStarted, trackSignupCompleted } from '../lib/analytics';
import { getUtm } from '../lib/utm';
import { ProgressBar, COLOR, TOTAL_STEPS } from '../components/onboarding/atoms';
import Step1Basics from '../components/onboarding/Step1Basics';
import Step2Origin from '../components/onboarding/Step2Origin';
import Step3Destination from '../components/onboarding/Step3Destination';
import Step4Household from '../components/onboarding/Step4Household';
import Step5Immigration from '../components/onboarding/Step5Immigration';
import Step6Lifestyle from '../components/onboarding/Step6Lifestyle';

/**
 * StartFreeTrial - Mundalo's 6-step intake wizard.
 *
 *   Step 1: Basic info (name, email, password)        → POST /api/auth/free-trial-signup
 *   Step 2: Origin (US state, city, citizenship)      ─┐
 *   Step 3: Destination + timeline                     │
 *   Step 4: Household (move type, kids, pets, car, $)  ├─► POST /api/auth/onboarding (final submit)
 *   Step 5: Immigration & legal      (skippable)       │
 *   Step 6: Lifestyle priorities     (skippable)      ─┘
 *
 * Per-step UI lives under /components/onboarding/Step{N}.js - this coordinator
 * owns the form state, validation, navigation, and the final submit.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', password: '',
  terms_accepted: false, email_consent: false,
  promo_code: '',                                                // iter 98
  current_us_state: '', current_city: '', us_citizenship_status: '', mexican_citizen: false,
  destination_decided: null,
  destination_city: '',
  lifestyle_preferences: [],
  move_timeline: '',
  move_type: '',
  children_coming: false, children_count: 0, children_ages: '',
  pets_coming: false, pets_details: '',
  vehicle_coming: false, vehicle_plates: '', vehicle_type: '',
  monthly_budget_usd: '',
  visa_goal: '', remote_work: '', has_rfc: '', property_intent: '',
  priorities: [], mexico_visits: '', referral_source: '',
};

const StartFreeTrial = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // GA4 - signup_started fires once on mount (landing on /start)
  useEffect(() => {
    trackSignupStarted();
  }, []);

  // Capture ?ref=<user_id> on first mount (iter 73 - Peer Circles invites).
  // Stored in sessionStorage so it survives the multi-step wizard and
  // gets attached to the signup payload on Step 1 submit.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const refParam = (params.get('ref') || '').trim();
      if (refParam) {
        sessionStorage.setItem('mundalo_ref', refParam);
      }
    } catch { /* noop - bad URL or storage disabled */ }
  }, []);

  // iter 182.g - Handle return from Stripe Checkout. Stripe hits
  // /start-trial/complete?session_id=cs_test_... which is routed here
  // via App.js. We finalize the account, then resume the intake wizard
  // at Step 2 using the sessionStorage-cached form.
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (!(path.endsWith('/start-trial/complete') && params.get('session_id'))) return;
    setLoading(true);
    (async () => {
      try {
        const session_id = params.get('session_id');
        const res = await fetch(`${API_URL}/api/auth/free-trial-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id }),
        });
        const data = await res.json();
        if (data.success && data.user) {
          if (data.auth_token) localStorage.setItem('auth_token', data.auth_token);
          setUser({ ...data.user, role: data.user.role || 'free_trial' });
          try {
            const cached = JSON.parse(sessionStorage.getItem('mundalo_pending_signup') || '{}');
            if (cached.form) setForm({ ...EMPTY_FORM, ...cached.form });
            sessionStorage.removeItem('mundalo_pending_signup');
          } catch { /* noop */ }
          trackSignupCompleted();
          // Land the user on step 2 to finish the intake wizard.
          window.history.replaceState({}, '', '/start-trial');
          setStep(2);
        } else {
          setErrors({ general: 'We couldn\'t confirm your subscription. Please try again or contact support.' });
          window.history.replaceState({}, '', '/start-trial');
        }
      } catch {
        setErrors({ general: 'Network error confirming your subscription. Please refresh.' });
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter 182.g - Show a friendly banner if user bailed out of Stripe Checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'cancelled') {
      setErrors({ general: 'Checkout cancelled. Your account is saved - complete signup any time.' });
      window.history.replaceState({}, '', '/start-trial');
    }
  }, []);

  const upd = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const togglePriority = (p) => {
    setForm((prev) => {
      const has = prev.priorities.includes(p);
      if (has) return { ...prev, priorities: prev.priorities.filter((x) => x !== p) };
      if (prev.priorities.length >= 3) return prev;
      return { ...prev, priorities: [...prev.priorities, p] };
    });
  };

  const toggleLifestyle = (p) => {
    setForm((prev) => {
      const has = prev.lifestyle_preferences.includes(p);
      return {
        ...prev,
        lifestyle_preferences: has
          ? prev.lifestyle_preferences.filter((x) => x !== p)
          : [...prev.lifestyle_preferences, p],
      };
    });
  };

  // ---------- STEP 1 SUBMIT ----------
  const submitStep1 = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);
    try {
      const utm = getUtm();
      const ref = (() => {
        try { return sessionStorage.getItem('mundalo_ref') || null; } catch { return null; }
      })();
      const signup_source = (() => {
        try { return sessionStorage.getItem('mundalo_signup_source') || null; } catch { return null; }
      })();
      // iter 182.g - Step 1 now hands off to Stripe Checkout in
      // subscription mode. The user creates their account here, then
      // enters their card at Stripe. Only after `checkout.session.completed`
      // (webhook) or /free-trial-complete (browser redirect back) does the
      // user get portal access. If they abandon Checkout, their pending
      // user row stays until they retry.
      const origin_url = window.location.origin;
      const res = await fetch(`${API_URL}/api/auth/free-trial-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          terms_accepted: !!form.terms_accepted,
          email_consent: !!form.email_consent,
          promo_code: (form.promo_code || '').trim() || undefined,
          utm, ref, signup_source, origin_url,
        }),
      });
      // iter 182.h - Parse JSON defensively. If the backend returns a
      // 5xx that gets rewritten by the platform proxy into an HTML
      // error page (e.g. "502: Bad gateway"), `res.json()` throws and
      // the old catch surfaced a misleading "Network error". Now we
      // detect that case, log a diagnostic ref, and show a clear
      // signup-service-unavailable message. Costs us zero real signups
      // to a hidden config failure.
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (res.ok && data && data.success && data.checkout_url) {
        // Stash the pending form so we can pick up the wizard at step 2
        // once the browser returns from Stripe.
        try {
          sessionStorage.setItem('mundalo_pending_signup', JSON.stringify({
            form, session_id: data.session_id,
          }));
        } catch { /* noop */ }
        // Hand off to Stripe.
        window.location.href = data.checkout_url;
        return;
      }
      if (data && data.errors) {
        setErrors(data.errors);
      } else if (data && data.detail) {
        setErrors({ general: data.detail });
      } else if (!res.ok) {
        // 5xx from origin got rewritten to an HTML error page by the
        // proxy - we have no parseable body. Surface a clear message
        // instead of "Network error" so users know it's on our side
        // and support can trace it.
        // eslint-disable-next-line no-console
        console.error('[free-trial-checkout] non-JSON error response', {
          status: res.status, statusText: res.statusText,
        });
        setErrors({
          general: `Signup is temporarily unavailable (ref: HTTP ${res.status}). Our team has been notified - please try again in a few minutes or email support@mundalo.com.`,
        });
      } else {
        setErrors({ general: 'Something went wrong. Please try again.' });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[free-trial-checkout] fetch failed', err);
      setErrors({ general: 'Network error - check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  // ---------- FINAL ONBOARDING SUBMIT ----------
  const submitOnboarding = async () => {
    setErrors({});
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        current_us_state: form.current_us_state,
        current_city: form.current_city,
        us_citizenship_status: form.us_citizenship_status,
        mexican_citizen: !!form.mexican_citizen,
        destination_decided: form.destination_decided === true,
        destination_city: form.destination_city.trim(),
        lifestyle_preferences: form.destination_decided === false ? form.lifestyle_preferences : [],
        move_timeline: form.move_timeline,
        move_type: form.move_type,
        children_coming: !!form.children_coming,
        children_count: form.children_coming ? Number(form.children_count || 0) : 0,
        children_ages: form.children_ages,
        pets_coming: !!form.pets_coming,
        pets_details: form.pets_details,
        vehicle_coming: !!form.vehicle_coming,
        vehicle_plates: form.vehicle_plates,
        vehicle_type: form.vehicle_type,
        monthly_budget_usd: form.monthly_budget_usd,
        visa_goal: form.visa_goal,
        remote_work: form.remote_work,
        has_rfc: form.has_rfc,
        property_intent: form.property_intent,
        priorities: form.priorities,
        mexico_visits: form.mexico_visits,
        referral_source: form.referral_source,
      };
      const res = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      // Iter 65 hardening: never blow up the catch with "Network error" when
      // the request actually completed. Production was returning HTML 502/504
      // pages from the proxy on slow onboarding requests, and `res.json()`
      // throwing made it look like a network failure. Read the body as text
      // first so we can surface the real status to the user.
      const rawBody = await res.text();
      let data;
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch {
        // Non-JSON response - likely a gateway error page. Show the status.
        if (res.status >= 500) {
          setErrors({
            general: `Server hiccup (HTTP ${res.status}). Your answers weren't saved - please tap Finish again in a moment.`,
          });
        } else if (res.status === 401 || res.status === 403) {
          setErrors({
            general: 'Your session expired. Please sign in again to finish onboarding.',
          });
        } else {
          setErrors({
            general: `Couldn't save your answers (HTTP ${res.status}). Please try again - if it persists, write us at hello@mundalo.com.`,
          });
        }
        return;
      }

      if (data.success) {
        trackSignupCompleted({
          destination_city: form.destination_city,
          move_type: form.move_type,
        });
        navigate('/portal/dashboard?welcome=trial', { replace: true });
      } else if (data.errors) {
        setErrors(data.errors);
      } else if (res.status === 401) {
        setErrors({ general: 'Your session expired. Please sign in again.' });
      } else {
        setErrors({ general: data.detail || 'Could not save your answers. Please try again.' });
      }
    } catch (err) {
      // Genuine network failure (DNS/offline/CORS) - distinguish from a
      // server-side error so the user knows it's worth retrying.
      // eslint-disable-next-line no-console
      console.error('[onboarding] submit network failure:', err);
      setErrors({ general: 'Network error - check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  // ---------- STEP VALIDATION ----------
  const validateBeforeAdvance = () => {
    const e = {};
    if (step === 3) {
      if (form.destination_decided === null) e.destination_decided = 'Pick one option';
      else if (form.destination_decided === true && !form.destination_city.trim()) {
        e.destination_city = 'Type where in Mexico (or pick "Not sure yet" above)';
      } else if (form.destination_decided === false && form.lifestyle_preferences.length === 0) {
        e.lifestyle_preferences = 'Pick at least one lifestyle preference';
      }
      if (!form.move_timeline) e.move_timeline = 'Pick a timeline';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateBeforeAdvance()) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const goBack = () => {
    setErrors({});
    if (step > 1) setStep(step - 1);
  };

  const skip = () => {
    if (step === 5) setStep(6);
    else if (step === 6) submitOnboarding();
  };

  return (
    <div
      className="min-h-screen flex items-start sm:items-center justify-center px-4 sm:px-6 py-8 sm:py-10"
      style={{ backgroundColor: COLOR.canvas, color: COLOR.navy }}
      data-testid="start-page-root"
    >
      <SEO
        title="Start your 15-day free trial - Mundalo"
        description="Move abroad. Live freely. Tell us about your move and Mundalo welcomes you into the community."
        path="/start"
      />
      <div className="w-full max-w-lg">
        <div
          className="border p-5 sm:p-8 rounded-2xl"
          style={{
            backgroundColor: COLOR.card,
            borderColor: COLOR.border,
            boxShadow: '0 8px 30px rgba(10,31,46,0.06)',
          }}
        >
          <div className="text-center mb-4">
            <Link to="/" className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E]" data-testid="start-wordmark">
              Mundalo
            </Link>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mt-1">
              Move abroad · live freely
            </p>
          </div>

          <ProgressBar step={step} />

          {errors.general && (
            <div
              className="mb-4 p-3 text-sm flex items-center gap-2 border rounded-lg"
              style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2', color: COLOR.error }}
              data-testid="start-general-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {errors.general}
            </div>
          )}

          {step === 1 && (
            <Step1Basics
              form={form} errors={errors} upd={upd} setField={setField} loading={loading}
              showPassword={showPassword} setShowPassword={setShowPassword}
              onSubmit={submitStep1}
            />
          )}
          {step === 2 && (
            <Step2Origin form={form} errors={errors} upd={upd} setField={setField} />
          )}
          {step === 3 && (
            <Step3Destination
              form={form} errors={errors} upd={upd} setField={setField}
              toggleLifestyle={toggleLifestyle}
            />
          )}
          {step === 4 && (
            <Step4Household form={form} upd={upd} setField={setField} />
          )}
          {step === 5 && (
            <Step5Immigration form={form} setField={setField} />
          )}
          {step === 6 && (
            <Step6Lifestyle
              form={form} errors={errors} upd={upd} setField={setField}
              togglePriority={togglePriority}
            />
          )}

          {step > 1 && (
            <div className="mt-6 space-y-3">
              {step === TOTAL_STEPS ? (
                <button type="button" onClick={submitOnboarding} disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 font-mono uppercase text-[12px] tracking-[0.15em] font-semibold rounded-full transition disabled:opacity-60"
                  style={{ backgroundColor: COLOR.gold, color: COLOR.navy }}
                  data-testid="onboarding-submit">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? 'Building your blueprint…' : 'Generate my blueprint'}
                  {!loading && <Check className="w-4 h-4" />}
                </button>
              ) : (
                <button type="button" onClick={goNext} disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 font-mono uppercase text-[12px] tracking-[0.15em] font-semibold rounded-full transition disabled:opacity-60"
                  style={{ backgroundColor: COLOR.gold, color: COLOR.navy }}
                  data-testid="onboarding-continue">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              )}

              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={goBack}
                  className="inline-flex items-center gap-1 text-[#5A6B73] hover:text-[#0A1F2E] transition font-mono uppercase tracking-[0.12em]"
                  data-testid="onboarding-back">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>

                {(step === 5 || step === 6) && (
                  <button type="button" onClick={skip} disabled={loading}
                    className="text-[#5A6B73] hover:text-[#0A1F2E] transition font-mono uppercase tracking-[0.12em] disabled:opacity-50"
                    data-testid="onboarding-skip">
                    Skip for now →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs mt-4 text-[#5A6B73]">
          You can refine these answers anytime from your portal.
        </p>
      </div>
    </div>
  );
};

export default StartFreeTrial;
